import {InputBaseComponentProps, TextField, TextFieldProps, Tooltip,} from '@mui/material';
import deLocale from 'date-fns/locale/nb';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {DatePicker, DatePickerProps, LocalizationProvider} from '@mui/x-date-pickers';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {checkIsValidInputDateFormat, formatDate, isValidDate,} from '../../../utils/dateUtils';
import {GridTemplateSize} from '../Grid/InlineGridTemplate';
import {parseISO} from 'date-fns';
import {FormInputEventHandler, markRequiredLabelRed, showRequiredMark,} from '../../../utils/formUtils';
import {useEditPanelContext} from '../../components/AdminDataGrid/EditPanelContext';
import {TEntity} from '../../../../models/entity';
import {EditorModus} from '../../components/EditDialog/EditDialog';
import EventIcon from '@mui/icons-material/Event';
import clsx from 'clsx';
import {redRequiredLabel} from '../TextField/TextField';

export type CustomDatePickerProps = Omit<DatePickerProps<Date, Date>,
    'error' | 'onChange' | 'renderInput'> &
    Pick<TextFieldProps, 'style'> & {
    name: string;
    error?: string;
    showErrorExplanation?: boolean;
    required?: boolean;
    disableValidation?: boolean;
    placeholder?: string;
    setOnlyDate?: boolean;
    clearable?: boolean;
    validateField?: (fieldValue: any, key: string, setValidation?: boolean) => boolean | undefined;
    mode?: EditorModus;
    getErrors?: (date: Date | null) => DateError[];
    handleFieldValidation?(
        fieldName: string,
        isValid: boolean,
        errorMessage?: string
    ): void;
} & (ReadOnlyProps | EnabledProps);

type ReadOnlyProps = {
    readOnly: true;
    onChange?: (evt: FormInputEventHandler) => void;
};
type EnabledProps = {
    readOnly?: false;
    onChange: (evt: FormInputEventHandler) => void;
};

export type DateErrorType = 'format' | 'limit' | 'required';
export type DateError = { msg?: string; type: DateErrorType };

const getParseDate = (input: Date | undefined | string) => typeof input === 'string' ? parseISO(input) : input;

const DatePickerControl = React.memo((props: CustomDatePickerProps) => {
    const {
        value,
        name,
        error,
        showErrorExplanation = false,
        onChange,
        className,
        style,
        validateField: validateFieldCustom,
        disableValidation,
        placeholder,
        required,
        minDate: inputMinDate,
        maxDate: inputMaxDate,
        handleFieldValidation: handleFieldValidationCustom,
        mode,
        clearable = !required,
        ...other
    } = props;

    const [inputDate, setInputDate] = useState<Date | null>(null);

    const minDate = useMemo(() => getParseDate(inputMinDate), [inputMinDate]);
    const maxDate = useMemo(() => getParseDate(inputMaxDate), [inputMaxDate]);

    const convertToDefEventPara = (n: string, value: any) => {
        return {
            target: {
                name: n,
                value: value,
            },
        };
    };

    const [errors, setErrors] = useState<DateError[]>([]);
    const [isValidFormat, setIsValidFormat] = useState(true);

    const {
        mode: contextMode,
        validateField: validateContextField,
        handleFieldValidation: handleFieldContextValidation
    } =
        useEditPanelContext<TEntity>();

    const validateField = validateFieldCustom ?? validateContextField;
    const handleFieldValidation = handleFieldValidationCustom ?? handleFieldContextValidation;


    const getLimitErrors = useCallback(
        (date: Date | null): DateError[] => {
            if (date == null) {
                if (required || !clearable) return [{type: 'limit', msg: 'Dato må være satt'}];
                else return [];
            }
            const err: DateError[] = [];
            if (minDate) {
                const isLimitError =
                    new Date(minDate).setHours(0, 0, 0, 0) >
                    new Date(date).setHours(0, 0, 0, 0);
                if (isLimitError)
                    err.push({
                        type: 'limit',
                        msg: `Dato må være større enn ${formatDate(minDate)}`,
                    });
            }
            if (maxDate) {
                const isLimitError =
                    new Date(maxDate).setHours(0, 0, 0, 0) <
                    new Date(date).setHours(0, 0, 0, 0);
                if (isLimitError)
                    err.push({
                        type: 'limit',
                        msg: `Dato må være mindre enn ${formatDate(maxDate)}`,
                    });
            }

            return err;
        },
        [minDate, maxDate, required, clearable]
    );

    const checkIsValidDateFormat = useCallback((date: Date | null, keyboardValue?: string) => {
        return (
            (!date || isValidDate(date)) && (!keyboardValue || checkIsValidInputDateFormat(keyboardValue))
        );
    }, []);


    const validateFormat = useCallback((date: Date | null, keyboardValue?: string) => {
        const isValidDate = checkIsValidDateFormat(date, keyboardValue);
        setIsValidFormat(isValidDate);
        return isValidDate;
    }, [checkIsValidDateFormat]);

    const validateInput = useCallback((i: { date: Date | null, keyboardValue?: string, notifyUser: boolean, notifyForm: boolean }) => {
        let err = getLimitErrors(i.date);
        (props.getErrors?.(i.date) ?? []).map(r => err.push(r));
        if (!validateFormat(i.date, i.keyboardValue)) err.push({type: 'format', msg: 'Feil dato format'});
        handleFieldValidation?.(name, err.length === 0, err[0]?.msg);
        setErrors(err);
        validateField(i.date, name, i.notifyForm, i.notifyUser, err[0]?.msg ? {name: err[0].msg} : undefined);
    }, [getLimitErrors, handleFieldValidation, name, validateField, validateFormat]);


    function handleInputDateChange(date: Date | null, keyboardValue?: string) {
        onChange?.(convertToDefEventPara(name, date));
        validateInput({date, keyboardValue, notifyForm: true, notifyUser: false});
        setInputDate(date);
    }

    useEffect(() => {
        setInputDate(value ?? null);
    }, [value]);

    function handleLoseFocus(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) {
        if(e.currentTarget.value?.length === 0)
            validateInput({date: props.value, keyboardValue: e.currentTarget.value, notifyForm: true, notifyUser: true});
    }

    const {requiredLabelStyle} = redRequiredLabel();
    const disabled = mode === EditorModus.Vis || contextMode === EditorModus.Vis || other.disabled;
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={deLocale}>
            <DatePicker
                {...other}
                value={inputDate}
                disabled={disabled}
                mask="__.__.____"
                inputFormat="dd.MM.yyyy"
                minDate={minDate}
                maxDate={maxDate}
                components={{
                    OpenPickerIcon: (() => (
                        <EventIcon color={disabled ? 'disabled' : 'primary'}/>
                    )) as any,
                }}
                componentsProps={{
                    leftArrowButton: {title: ''},
                    rightArrowButton: {title: ''},
                }}
                PaperProps={{
                    sx: {
                        '& .MuiCalendarPicker-root .PrivatePickersFadeTransitionGroup-root':
                            {
                                textTransform: 'capitalize',
                            },
                    },
                }}
                renderInput={(params: any) => (
                    <Tooltip
                        title={
                            !showErrorExplanation || !error ? (
                                ''
                            ) : (
                                <div style={{whiteSpace: 'pre-line'}}>{error}</div>
                            )
                        }
                    >
                        <TextField
                            {...params}
                            required={
                                !disableValidation &&
                                showRequiredMark(inputDate, mode ?? contextMode, error, required)
                            }
                            className={clsx(
                                className,
                                markRequiredLabelRed(inputDate, mode ?? contextMode) ? requiredLabelStyle : ''
                            )}
                            error={!!error || (errors && errors.length > 0) || !isValidFormat}
                            disabled={disabled && !error}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            autoComplete={'off'}
                            helperText={null}
                            onBlur={handleLoseFocus}
                            style={{
                                minWidth: style?.minWidth ?? GridTemplateSize.kode,
                                maxWidth: style?.maxWidth ?? GridTemplateSize.datePicker,
                                ...style,
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    color: 'rgba(0, 0, 0, 0.6)',
                                },
                            }}

                            inputProps={{
                                ...params.inputProps,
                                type: 'text',
                                placeholder: placeholder ?? 'dd.mm.åååå',
                                ...(props.InputProps as InputBaseComponentProps),
                            }}
                        />
                    </Tooltip>
                )}
                onChange={(date, inputValue) => handleInputDateChange(date, inputValue)}
            />
        </LocalizationProvider>
    );
});


export default DatePickerControl;
